import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Form,
  Typography,
  message,
  Spin,
  Row,
  Col,
  Tag,
  Alert,
  Space,
  Modal,
  Switch,
  InputNumber,
  Empty,
  Badge,
  Tooltip,
  Divider,
  Select,
} from "antd";
import {
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { filterByCurrentProject } from "../store/slices/dataSourcesSlice";
import { apiUtils } from "../lib/api";

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

interface Workflow {
  name: string;
  id: string;
  description: string;
  version: string;
  requiredInputs: string[];
  optionalInputs: string[];
}

interface WorkflowResult {
  success: boolean;
  workflowName: string;
  executionId: string;
  output: any;
  results: Record<string, any>;
  executionPath: string[];
  error?: string;
  metadata: {
    workflowId: string;
    workflowVersion: string;
    executionTime: number;
    timestamp: string;
  };
}

interface WorkflowExecution {
  workflowName: string;
  status: "running" | "success" | "failed";
  startTime: Date;
  result?: WorkflowResult;
}

const SystemWorkflowsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((state) => state.project);
  const dataSourcesState = useAppSelector(
    (state) =>
      state?.dataSources || {
        dataSources: [],
        currentProjectDataSources: [],
        isLoading: false,
        error: null,
      }
  );

  const dataSources = Array.isArray(dataSourcesState?.dataSources)
    ? dataSourcesState.dataSources
    : [];
  const dataSourcesLoading = dataSourcesState?.isLoading || false;
  
  // Use the filtered data sources from Redux state
  const currentProjectDataSources = Array.isArray(dataSourcesState?.currentProjectDataSources)
    ? dataSourcesState.currentProjectDataSources
    : [];

  // Debug datasources (can be removed in production)
  console.log("Datasources Debug:", {
    currentProject: currentProject?.id,
    totalDataSources: dataSources.length,
    currentProjectDataSources: currentProjectDataSources.length,
    hasDataSources: Array.isArray(dataSources),
    dataSourcesInitialized: dataSourcesState?.isInitialized,
    reduxCurrentProjectDataSources: dataSourcesState?.currentProjectDataSources?.length || 0,
  });

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [executions, setExecutions] = useState<Map<string, WorkflowExecution>>(
    new Map()
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null
  );
  const [parameterModal, setParameterModal] = useState(false);
  const [resultModal, setResultModal] = useState(false);
  const [currentResult, setCurrentResult] = useState<WorkflowResult | null>(
    null
  );
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAvailableWorkflows();
  }, []);

  // Filter data sources when current project changes
  useEffect(() => {
    // Only filter if data sources are initialized and available
    if (dataSourcesState?.isInitialized && Array.isArray(dataSourcesState.dataSources)) {
      if (currentProject) {
        dispatch(filterByCurrentProject(currentProject.id));
      } else {
        dispatch(filterByCurrentProject(null));
      }
    }
  }, [currentProject?.id, dispatch, dataSourcesState?.isInitialized]);

  const fetchAvailableWorkflows = async () => {
    try {
      setLoading(true);
      const response = await apiUtils.backend.get("/workflows/available");
      setWorkflows(response.data.workflows || []);
    } catch (error) {
      message.error("Failed to fetch available workflows");
      console.error("Error fetching workflows:", error);
    } finally {
      setLoading(false);
    }
  };

  const executeWorkflow = async (workflow: Workflow, parameters: any) => {
    const executionId = `${workflow.name}-${Date.now()}`;

    // Add to executions map
    const execution: WorkflowExecution = {
      workflowName: workflow.name,
      status: "running",
      startTime: new Date(),
    };
    setExecutions((prev) => new Map(prev.set(executionId, execution)));

    try {
      // Clean up parameters
      const cleanedParams = Object.keys(parameters)
        .filter(
          (key) => parameters[key] !== undefined && parameters[key] !== ""
        )
        .reduce((acc, key) => {
          acc[key] = parameters[key];
          return acc;
        }, {} as any);

      const response = await apiUtils.backend.post("/workflows/execute", {
        workflowName: workflow.name,
        parameters: cleanedParams,
        timeout: 300000,
      });

      const result = response.data;

      // Update execution status
      setExecutions(
        (prev) =>
          new Map(
            prev.set(executionId, {
              ...execution,
              status: result.success ? "success" : "failed",
              result,
            })
          )
      );

      if (result.success) {
        message.success(`Workflow '${workflow.name}' executed successfully!`);
      } else {
        message.error(`Workflow execution failed: ${result.error}`);
      }

      // Show result
      setCurrentResult(result);
      setResultModal(true);
    } catch (error: any) {
      // Update execution status
      setExecutions(
        (prev) =>
          new Map(
            prev.set(executionId, {
              ...execution,
              status: "failed",
              result: {
                success: false,
                workflowName: workflow.name,
                executionId: "",
                output: null,
                results: {},
                executionPath: [],
                error: error.response?.data?.message || error.message,
                metadata: {
                  workflowId: workflow.id,
                  workflowVersion: workflow.version,
                  executionTime: 0,
                  timestamp: new Date().toISOString(),
                },
              },
            })
          )
      );

      message.error("Failed to execute workflow");
      console.error("Error executing workflow:", error);
    }
  };

  const handleQuickExecute = (workflow: Workflow) => {
    if (workflow.requiredInputs.length === 0) {
      // Execute immediately if no required parameters
      executeWorkflow(workflow, {});
    } else {
      // Open parameter modal
      setSelectedWorkflow(workflow);
      setParameterModal(true);
      form.resetFields();
    }
  };

  const handleParameterSubmit = (values: any) => {
    if (selectedWorkflow) {
      executeWorkflow(selectedWorkflow, values);
      setParameterModal(false);
      setSelectedWorkflow(null);
    }
  };

  const renderParameterInput = (paramName: string, isRequired: boolean) => {
    const isNumber =
      paramName.includes("max") ||
      paramName.includes("Max") ||
      paramName.includes("timeout");
    const isBoolean =
      paramName.includes("enable") || paramName.includes("Enable");
    const isDataSource =
      paramName.toLowerCase().includes("datasource") ||
      paramName.toLowerCase().includes("data_source") ||
      paramName.toLowerCase().includes("dataSourceId");

    if (isDataSource) {
      return (
        <Form.Item
          key={paramName}
          name={paramName}
          label="Data Source"
          rules={
            isRequired
              ? [{ required: true, message: "Data source is required" }]
              : []
          }
        >
          <Select
            placeholder="Select a data source"
            loading={dataSourcesLoading}
            notFoundContent={
              currentProject
                ? "No data sources found for this project"
                : "Select a project first"
            }
            dropdownStyle={{ zIndex: 1050 }}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            {/* Show current project datasources first, then all if none found */}
            {currentProjectDataSources &&
            currentProjectDataSources.length > 0 ? (
              currentProjectDataSources.map((dataSource) => (
                <Option key={dataSource.id} value={dataSource.id}>
                  <Space>
                    <Tag
                      color={
                        dataSource.connectorType === "mssql" ? "blue" : "green"
                      }
                    >
                      {dataSource.connectorType.toUpperCase()}
                    </Tag>
                    {dataSource.name}
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      (Project)
                    </Text>
                  </Space>
                </Option>
              ))
            ) : Array.isArray(dataSources) && dataSources.length > 0 ? (
              <>
                <Option disabled key="header-all">
                  <Text
                    type="secondary"
                    style={{ fontSize: "11px", fontStyle: "italic" }}
                  >
                    All Available Data Sources:
                  </Text>
                </Option>
                {dataSources
                  .filter((ds) => ds && ds.isActive)
                  .map((dataSource) => (
                    <Option key={dataSource.id} value={dataSource.id}>
                      <Space>
                        <Tag
                          color={
                            dataSource.connectorType === "mssql"
                              ? "blue"
                              : "green"
                          }
                        >
                          {dataSource.connectorType.toUpperCase()}
                        </Tag>
                        {dataSource.name}
                        <Text type="secondary" style={{ fontSize: "10px" }}>
                          ({dataSource.projectId || "No Project"})
                        </Text>
                      </Space>
                    </Option>
                  ))}
              </>
            ) : (
              <Option disabled value="">
                {dataSourcesLoading
                  ? "Loading data sources..."
                  : "No data sources available"}
              </Option>
            )}
          </Select>
        </Form.Item>
      );
    }

    if (isBoolean) {
      return (
        <Form.Item
          key={paramName}
          name={paramName}
          label={paramName}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      );
    }

    if (isNumber) {
      return (
        <Form.Item
          key={paramName}
          name={paramName}
          label={paramName}
          rules={
            isRequired
              ? [{ required: true, message: `${paramName} is required` }]
              : []
          }
        >
          <InputNumber style={{ width: "100%" }} min={1} max={1000} />
        </Form.Item>
      );
    }

    return (
      <Form.Item
        key={paramName}
        name={paramName}
        label={paramName}
        rules={
          isRequired
            ? [{ required: true, message: `${paramName} is required` }]
            : []
        }
      >
        <Input placeholder={`Enter ${paramName}`} />
      </Form.Item>
    );
  };

  const getWorkflowStatus = (workflowName: string) => {
    const executions_array = Array.from(executions.values());
    const workflowExecutions = executions_array.filter(
      (e) => e.workflowName === workflowName
    );

    if (workflowExecutions.length === 0) return null;

    const running = workflowExecutions.some((e) => e.status === "running");
    if (running) return "running";

    const lastExecution = workflowExecutions.sort(
      (a, b) => b.startTime.getTime() - a.startTime.getTime()
    )[0];

    return lastExecution.status;
  };

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderWorkflowCard = (workflow: Workflow) => {
    const status = getWorkflowStatus(workflow.name);
    const hasRequiredParams = workflow.requiredInputs.length > 0;

    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={workflow.name}>
        <Card
          hoverable
          className="workflow-card"
          style={{ height: "100%", position: "relative" }}
          bodyStyle={{ padding: "16px" }}
          actions={[
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleQuickExecute(workflow)}
              loading={status === "running"}
              style={{ width: "90%" }}
            >
              {status === "running" ? "Running..." : "Execute"}
            </Button>,
          ]}
        >
          {/* Status Badge */}
          {status && (
            <div style={{ position: "absolute", top: 8, right: 8 }}>
              {status === "running" && (
                <Badge status="processing" text={<Spin size="small" />} />
              )}
              {status === "success" && <Badge status="success" />}
              {status === "failed" && <Badge status="error" />}
            </div>
          )}

          <div style={{ marginBottom: "12px" }}>
            <Title level={4} style={{ margin: 0, fontSize: "16px" }}>
              {workflow.name}
            </Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              v{workflow.version}
            </Text>
          </div>

          <Paragraph
            style={{
              fontSize: "13px",
              marginBottom: "12px",
              minHeight: "40px",
              color: "var(--secondary-color, #6b7280)",
            }}
            ellipsis={{ rows: 2 }}
          >
            {workflow.description}
          </Paragraph>

          <Space size="small" style={{ marginBottom: "8px" }}>
            {hasRequiredParams && (
              <Tooltip title="Has required parameters">
                <Tag icon={<SettingOutlined />} color="orange">
                  {workflow.requiredInputs.length} Required
                </Tag>
              </Tooltip>
            )}
            {workflow.optionalInputs.length > 0 && (
              <Tooltip title="Has optional parameters">
                <Tag color="blue">
                  {workflow.optionalInputs.length} Optional
                </Tag>
              </Tooltip>
            )}
          </Space>
        </Card>
      </Col>
    );
  };

  const renderResultModal = () => {
    if (!currentResult) return null;

    return (
      <Modal
        title={
          <Space>
            {currentResult.success ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            )}
            <span>Execution Result - {currentResult.workflowName}</span>
            <Tag color={currentResult.success ? "green" : "red"}>
              {currentResult.success ? "SUCCESS" : "FAILED"}
            </Tag>
          </Space>
        }
        open={resultModal}
        onCancel={() => setResultModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setResultModal(false)}>
            Close
          </Button>,
        ]}
      >
        {currentResult.success ? (
          <div>
            {/* Task Suggestion specific rendering */}
            {currentResult.workflowName === "task-suggestion" &&
              currentResult.output?.assetNum && (
                <div style={{ marginBottom: "24px" }}>
                  <Title level={5}>Asset Information</Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Text strong>Asset Number: </Text>
                      <Text>{currentResult.output.assetNum}</Text>
                    </Col>
                    <Col span={12}>
                      <Text strong>Asset Type: </Text>
                      <Text>{currentResult.output.assetType}</Text>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>
                    Task Suggestions ({currentResult.output.totalSuggestions}{" "}
                    total)
                  </Title>

                  {currentResult.output.sqlSuggestions &&
                    currentResult.output.sqlSuggestions.count > 0 && (
                      <div style={{ marginBottom: "16px" }}>
                        <Text strong>
                          SQL-Based Suggestions (
                          {currentResult.output.sqlSuggestions.count}):
                        </Text>
                        <ul style={{ marginTop: "8px" }}>
                          {currentResult.output.sqlSuggestions.beautified.map(
                            (suggestion: string, index: number) => (
                              <li key={index} style={{ marginBottom: "4px" }}>
                                <Text>{suggestion}</Text>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                  {currentResult.output.ragSuggestions &&
                    currentResult.output.ragSuggestions.count > 0 && (
                      <div style={{ marginBottom: "16px" }}>
                        <Text strong>
                          RAG-Based Suggestions (
                          {currentResult.output.ragSuggestions.count}):
                        </Text>
                        <ul style={{ marginTop: "8px" }}>
                          {currentResult.output.ragSuggestions.tasks.map(
                            (suggestion: string, index: number) => (
                              <li key={index} style={{ marginBottom: "4px" }}>
                                <Text>{suggestion}</Text>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              )}

            {/* Generic output rendering */}
            <div>
              <Text strong>Execution Details:</Text>
              <div
                style={{ marginTop: "8px", fontSize: "12px", color: "var(--secondary-color, #6b7280)" }}
              >
                <div>
                  Execution Time: {currentResult.metadata.executionTime}ms
                </div>
                <div>
                  Timestamp:{" "}
                  {new Date(currentResult.metadata.timestamp).toLocaleString()}
                </div>
              </div>

              <details style={{ marginTop: "16px" }}>
                <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
                  <Text strong>Raw Output Data</Text>
                </summary>
                <pre
                  style={{
                    background: "#f5f5f5",
                    padding: "12px",
                    borderRadius: "6px",
                    overflow: "auto",
                    maxHeight: "300px",
                    fontSize: "12px",
                  }}
                >
                  {JSON.stringify(currentResult.output, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        ) : (
          <Alert
            message="Execution Failed"
            description={currentResult.error}
            type="error"
            showIcon
          />
        )}
      </Modal>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            <SettingOutlined style={{ marginRight: "8px", color: "var(--primary-color, #13afe3)" }} />
            System Workflows
          </Title>
          {currentProject && (
            <Tag color="blue" style={{ fontSize: "12px" }}>
              Project: {currentProject.name}
            </Tag>
          )}
        </div>
        <Paragraph style={{ margin: 0, color: "var(--secondary-color, #6b7280)" }}>
          Execute and monitor system workflows. Click on any workflow card to
          run it directly.
          {currentProject &&
            ` Data sources filtered for ${currentProject.name}.`}
        </Paragraph>
      </div>

      {/* Controls */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col flex="auto">
          <Search
            placeholder="Search workflows by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: "400px" }}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAvailableWorkflows}
            loading={loading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Workflows Grid */}
      {filteredWorkflows.length === 0 ? (
        <Empty description="No workflows found" style={{ padding: "60px 0" }} />
      ) : (
        <Row gutter={[16, 16]}>{filteredWorkflows.map(renderWorkflowCard)}</Row>
      )}

      {/* Parameter Modal */}
      <Modal
        title={`Execute ${selectedWorkflow?.name}`}
        open={parameterModal}
        onCancel={() => {
          setParameterModal(false);
          setSelectedWorkflow(null);
        }}
        footer={null}
        width={600}
      >
        {selectedWorkflow && (
          <div>
            <Paragraph style={{ marginBottom: "24px" }}>
              {selectedWorkflow.description}
            </Paragraph>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleParameterSubmit}
            >
              {selectedWorkflow.requiredInputs.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <Text strong style={{ color: "#ff4d4f" }}>
                    Required Parameters:
                  </Text>
                  {selectedWorkflow.requiredInputs.map((param) =>
                    renderParameterInput(param, true)
                  )}
                </div>
              )}

              {selectedWorkflow.optionalInputs.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <Text strong>Optional Parameters:</Text>
                  {selectedWorkflow.optionalInputs.map((param) =>
                    renderParameterInput(param, false)
                  )}
                </div>
              )}

              <Form.Item style={{ marginBottom: 0, marginTop: "24px" }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<PlayCircleOutlined />}
                  >
                    Execute Workflow
                  </Button>
                  <Button onClick={() => setParameterModal(false)}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Result Modal */}
      {renderResultModal()}
    </div>
  );
};

export default SystemWorkflowsPage;
